import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Form, Input, Select, Checkbox, Card, Modal, App as AntdApp, Space } from 'antd';
import { useGroups } from '../../hooks/useGroups';
import { useAgeGroups } from '../../hooks/useAgeGroups';
import { useStudents } from '../../hooks/useStudents';
import { sendWpMessage, fetchWpInstanceStatus, ensureWpConnection } from '../../store/slices/wpConfigSlice';
import { useNavigate } from 'react-router-dom';

const { TextArea } = Input;

const StatusDot = ({ color }) => (
  <span style={{
    display: 'inline-block',
    width: 12,
    height: 12,
    borderRadius: '50%',
    background: color,
    marginRight: 8,
    verticalAlign: 'middle',
    boxShadow: `0 0 4px ${color}`
  }} />
);

const WpSendPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { message } = AntdApp.useApp();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [confirmVisible, setConfirmVisible] = useState(false);
  const [pendingValues, setPendingValues] = useState(null);
  const selectedBranch = useSelector((state) => state.branches.selectedBranch);
  const { groups = [] } = useGroups(selectedBranch?.id);
  const { ageGroups = [] } = useAgeGroups(selectedBranch?.id);
  const { students = [] } = useStudents(selectedBranch?.id);
  const { instance } = useSelector(state => state.wpConfig);

  useEffect(() => {
    if (selectedBranch?.id) {
      // İlk yükleme - bağlantıyı kontrol et
      dispatch(ensureWpConnection(selectedBranch.id));
    }
  }, [dispatch, selectedBranch?.id]);

  const wpStatus = instance?.status || null;
  const isConnected = wpStatus?.status === 'connected';

  // Seçim değiştiğinde diğerlerini temizle
  const handleFieldChange = (changedValues, allValues) => {
    const changedKey = Object.keys(changedValues)[0];
    if (changedKey === 'groupId') {
      form.setFieldsValue({ ageGroupId: undefined, studentIds: undefined, sendToAll: false });
    } else if (changedKey === 'ageGroupId') {
      form.setFieldsValue({ groupId: undefined, studentIds: undefined, sendToAll: false });
    } else if (changedKey === 'studentIds') {
      form.setFieldsValue({ groupId: undefined, ageGroupId: undefined, sendToAll: false });
    } else if (changedKey === 'sendToAll') {
      if (changedValues.sendToAll) {
        form.setFieldsValue({ groupId: undefined, ageGroupId: undefined, studentIds: undefined });
      }
    }
  };

  const handleFinish = (values) => {
    // Bağlı değilse uyar
    if (!isConnected) {
      message.error('WhatsApp bağlı değil! Lütfen önce WP bağla sayfasından bağlantı kurun.');
      return;
    }
    setPendingValues({ ...values, branchId: selectedBranch?.id });
    setConfirmVisible(true);
  };

  const handleConfirmSend = async () => {
    setLoading(true);
    setConfirmVisible(false);
    try {
      await dispatch(sendWpMessage(pendingValues)).unwrap();
      message.success('Mesaj gönderildi!');
      form.resetFields();
    } catch (err) {
      message.error(err || 'Mesaj gönderilemedi!');
    } finally {
      setLoading(false);
      setPendingValues(null);
    }
  };

  const handleEnsureConnection = async () => {
    if (selectedBranch?.id) {
      const result = await dispatch(ensureWpConnection(selectedBranch.id));
      if (result.payload?.isConnected) {
        message.success('WhatsApp bağlı!');
      } else {
        message.warning('WhatsApp bağlı değil. WP Bağla sayfasına gidin.');
      }
    }
  };

  return (
    <div style={{ maxWidth: 500, margin: '40px auto', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      {/* Bağlı/bağlı değil durumu ve butonu ortada, kutunun üstünde */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 32 }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
          {isConnected ? (
            <>
              <StatusDot color="#52c41a" />
              <span style={{ fontWeight: 600, fontSize: 18 }}>Bağlı</span>
            </>
          ) : (
            <>
              <StatusDot color="#ff4d4f" />
              <span style={{ fontWeight: 600, fontSize: 18 }}>Bağlı değil</span>
            </>
          )}
        </div>
        {!isConnected && (
          <Space>
            <Button type="dashed" onClick={() => navigate('/wp')}>
              WP Bağla sayfasına git
            </Button>
            <Button type="link" size="small" onClick={handleEnsureConnection}>
              Yenile
            </Button>
          </Space>
        )}
      </div>
      <Card title="WP Mesaj Gönder" bordered style={{ width: '100%' }}>
        <Form
          form={form}
          layout="vertical"
          onValuesChange={handleFieldChange}
          onFinish={handleFinish}
        >
          <Form.Item name="message" label="Mesaj" rules={[{ required: true, message: 'Mesaj giriniz' }]}>
            <TextArea rows={4} placeholder="Mesajınızı yazın..." />
          </Form.Item>
          <Form.Item name="groupId" label="Grup">
            <Select placeholder="Grup seçin" allowClear>
              {groups.map(group => (
                <Select.Option key={group.id} value={group.id}>{group.name}</Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="ageGroupId" label="Yaş Grubu">
            <Select placeholder="Yaş grubu seçin" allowClear>
              {ageGroups.map(ageGroup => (
                <Select.Option key={ageGroup.id} value={ageGroup.id}>{ageGroup.name}</Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="studentIds" label="Öğrenciler">
            <Select mode="multiple" placeholder="Öğrenci seçin" allowClear>
              {students.map(student => (
                <Select.Option key={student.id} value={student.id}>{student.fullName}</Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="sendToAll" valuePropName="checked">
            <Checkbox>Hepsine gönder</Checkbox>
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading} block disabled={!isConnected}>
              Gönder
            </Button>
          </Form.Item>
        </Form>
        <Modal
          open={confirmVisible}
          onCancel={() => setConfirmVisible(false)}
          onOk={handleConfirmSend}
          okText="Evet, Gönder"
          cancelText="Vazgeç"
          confirmLoading={loading}
        >
          Mesajı göndermek istediğinize emin misiniz?
        </Modal>
      </Card>
    </div>
  );
};

export default WpSendPage; 